import * as React from 'react';
import { InputGroup } from 'react-bootstrap';

import Tooltip from 'af-components/Tooltip';

import type { CellError } from '../../error.types';
import type { GlyphItem } from '../Glyph';
import Glyph from '../Glyph';
import styles from '../styles.module.scss';
import type { InputCellType, InputSharedProps } from '../types';

type FieldValue = string | number | undefined;

interface OwnProps {
	elementKey: string;
	value: FieldValue;
	name: string;
	/**
	 * Type of input cell can be: text, numeric, dollar cell
	 * Depending on type, design is decided
	*
	* @default text
	*/
	type?: InputCellType;
	placeholder?: string;
	className?: string;
	error?: CellError;
	readonly?: boolean;
}

type Props = OwnProps & InputSharedProps;

export const DisplayCell: React.FC<Props> = React.memo((props) => {
	const {
		placeholder,
		type = 'text',
		elementKey,
		className,
		value,
		error,
		readonly,
		prefixGlyphs = [],
		suffixGlyphs = [],
	} = props;

	// QA id is automatically generated to help with automated tests
	// We want to avoid accidentally forgetting to place an id on an element, making it harder for QA to write automated tests, so we keep it generated
	// Styling should never come from id, anyway
	const id = React.useMemo(() => (
		`accqa__input__display__${elementKey}`
	), [elementKey]);

	const getClassName = React.useMemo(() => {
		let cn = `${styles.input}`;

		if (className) {
			cn = `${cn} ${className}`;
		}

		if (type === 'dollar') {
			cn = `${cn} ${styles['dollar-value']}`;
		} else if (type === 'numeric') {
			cn = `${cn} ${styles['numeric-value']}`;
		} else if (type === 'quantity') {
			cn = `${cn} ${styles['money-or-qty-value']}`;
		} else if (type === 'percent') {
			cn = `${cn} ${styles['percent-value']}`;
		}
		if (error) {
			cn = `${cn} ${styles['with-error']}`;
			if (error.type === 'tooltip') {
				cn = `${cn} ${styles['with-tooltip']}`;
			} else if (error.type === 'required') {
				cn = `${cn} ${styles['error-placeholder']}`;
			}
		}
		if (readonly) {
			cn = `${cn} ${styles.isReadonly}`;
		}
		return cn;
	}, [className, type, error, readonly]);

	const resolvedPrefixGlyphs = React.useMemo<GlyphItem[]>(() => {
		const glyphs = [...prefixGlyphs];
		if (type === 'dollar') {
			glyphs.unshift({ type: 'icon', value: 'dollar' });
		}
		return glyphs;
	}, [prefixGlyphs, type]);

	const resolvedSuffixGlyphs = React.useMemo<GlyphItem[]>(() => {
		const glyphs = [...suffixGlyphs];
		if (type === 'percent') {
			glyphs.unshift({ type: 'text', value: '%' });
		}
		return glyphs;
	}, [suffixGlyphs, type]);

	const renderGlyph = React.useCallback((glyph: GlyphItem, index: number) => {
		return <Glyph glyph={glyph} key={index} />;
	}, []);

	const wrapperClassName = `${styles['input-wrapper']} ${readonly ? styles.isReadonly : ''}`;

	const renderCell = React.useMemo(() => {
		const placeholderInCell = error?.type === 'required'
			? error?.message
			: placeholder
				? placeholder
				: '-';

		return (
			<InputGroup className={wrapperClassName}>
				{!!resolvedPrefixGlyphs.length && (
					<div className={styles.glyphs}>
						{resolvedPrefixGlyphs.map(renderGlyph)}
					</div>
				)}
				<div
					className={`${styles['readonly-field']} ${getClassName}`}
					data-placeholder={placeholderInCell}
					id={id}
					key={elementKey}
				>
					{value?.toString().trim() ? value : ''}
				</div>
				{!!resolvedSuffixGlyphs.length && (
					<div className={styles.glyphs}>
						{resolvedSuffixGlyphs.map(renderGlyph)}
					</div>
				)}
				{error?.type === 'tooltip' && error.message && (
					<Tooltip message={error.message} placement="auto">
						<span className="icon-notification_status" />
					</Tooltip>
				)}
			</InputGroup>
		);
	}, [
		error?.type,
		error?.message,
		placeholder,
		wrapperClassName,
		resolvedPrefixGlyphs,
		renderGlyph,
		getClassName,
		id,
		elementKey,
		value,
		resolvedSuffixGlyphs,
	]);

	return renderCell;
});
